import EthereumLogo from '../assets/networks/ethereum-eth-logo.png'
import PolygonLogo from '../assets/networks/polygon-matic-logo.png'
import ArbitrumLogo from '../assets/networks/arbitrum-arb-logo.png'
import BinanceLogo from '../assets/networks/bnb-bnb-logo.png'
import OptimismLogo from '../assets/networks/optimism-ethereum-op-logo.png'
import BaseLogo from '../assets/networks/Base.svg'
import SXLogo from '../assets/networks/sx-network-logo.png'
import SOLLogo from '../assets/img/icons/cryptoicons/SVG/sol.svg'
import AVAXLogo from '../assets/img/icons/cryptoicons/SVG/avax.svg'
import CELOLogo from '../assets/celo_network.svg'
import CADARB from '../assets/currency/CADC_ARBITRUM.svg'
import CADBASE from '../assets/currency/CADC_BASE.svg'
import CADSOL from '../assets/currency/CADC_SOL.svg'
import CADETH from '../assets/currency/CADC_ETH.svg'
import CADPOLY from '../assets/currency/CADC_POLYGON.svg'
import USDPOLY from '../assets/currency/USDC_POLY.png'
import USDETH from '../assets/currency/USDC_ETH.png'
import USDARB from '../assets/currency/USDC_ARB.png'
import USDCAVAX from '../assets/currency/USDC_AVAX.png'
import USDCCELO from '../assets/currency/USDC_CELO.png'
import USDCBN from '../assets/currency/USDC_BSC.png'
import USDCBASE from '../assets/currency/USDC_BASE.svg'
import USDCOPTI from '../assets/currency/USDC_OP.png'
import USDCSX from '../assets/currency/USDC_SX.png'
import CADC from '../assets/currency/CADC.svg'
import USDC from '../assets/img/icons/cryptoicons/SVG/usdc.svg'
import USDCSOL from '../assets/currency/USDC_SOL.png'
import CAD from '../assets/currency/cad.png'
import {
  mainnet,
  arbitrum,
  optimism,
  polygon,
  base,
  bsc,
  avalanche,
  celo,
} from 'viem/chains'
import { http } from 'viem'
import { connectorsForWallets } from '@rainbow-me/rainbowkit'
import { createConfig } from 'wagmi'
import { injected } from 'wagmi/connectors'
import {
  rainbowWallet,
  metaMaskWallet,
  coinbaseWallet,
  walletConnectWallet,
  uniswapWallet,
  trustWallet,
} from '@rainbow-me/rainbowkit/wallets'
import {
  BLOCK_EXPLORER_URLS,
  getCadcContractAddress,
  getTokenContractsFor,
  getTokenExplorerUrl,
  type TokenLifecycle,
} from '@paytrie/common/chains'

import { SOLANA_CLUSTER, SolanaCluster } from './solanaCluster'
import { rabbyMobileWallet } from './rabbyMobileWallet'

type NetworkOption = {
  name: string
  icon: string
}

export const Networks = {
  ethereum: 'ETH',
  polygon: 'POLY',
  arbitrum: 'ARB',
  binance: 'BSC',
  optimism: 'OP',
  sx: 'SX',
  sol: 'SOL',
  base: 'BASE',
  avalanche: 'AVAX',
  celo: 'CELO',
} as const

export type NetworkValue = (typeof Networks)[keyof typeof Networks]

export const NetworkOptions: Record<NetworkValue, NetworkOption> = {
  [Networks.ethereum]: {
    name: 'Ethereum',
    icon: EthereumLogo,
  },
  [Networks.sol]: {
    name: 'Solana',
    icon: SOLLogo,
  },
  [Networks.binance]: {
    name: 'BSC',
    icon: BinanceLogo,
  },
  [Networks.polygon]: {
    name: 'Polygon',
    icon: PolygonLogo,
  },
  [Networks.base]: {
    name: 'Base',
    icon: BaseLogo,
  },
  [Networks.arbitrum]: {
    name: 'Arbitrum',
    icon: ArbitrumLogo,
  },
  [Networks.optimism]: {
    name: 'Optimism',
    icon: OptimismLogo,
  },
  [Networks.avalanche]: {
    name: 'Avalanche',
    icon: AVAXLogo,
  },
  [Networks.celo]: {
    name: 'Celo',
    icon: CELOLogo,
  },
  [Networks.sx]: {
    name: 'SX',
    icon: SXLogo,
  },
}

const chains = [
  mainnet,
  optimism,
  arbitrum,
  base,
  bsc,
  polygon,
  avalanche,
  celo,
] as const

const rainbowKitConnectors = connectorsForWallets(
  [
    {
      groupName: 'Suggested',
      wallets: [
        rainbowWallet,
        metaMaskWallet,
        rabbyMobileWallet,
        coinbaseWallet,
        walletConnectWallet,
        uniswapWallet,
        trustWallet,
      ],
    },
  ],
  {
    appName: 'Paytrie',
    projectId:
      import.meta.env.VITE_WALLET_CONNECT_PROJECT_ID ||
      '21627e108fd6feb1af48c3eb190400f2',
  }
)

export const rainbowKitConfig = createConfig({
  chains,
  transports: {
    [mainnet.id]: http(),
    [optimism.id]: http(),
    [arbitrum.id]: http(),
    [base.id]: http(),
    [bsc.id]: http(),
    [polygon.id]: http(),
    [avalanche.id]: http(),
    [celo.id]: http(),
  },
  connectors: [injected(), ...rainbowKitConnectors],
})

export function getNetwork(abr: string) {
  switch (abr) {
    case 'ETH':
      return Networks.ethereum
    case 'POLY':
      return Networks.polygon
    case 'ARB':
      return Networks.arbitrum
    case 'BSC':
      return Networks.binance
    case 'OP':
      return Networks.optimism
    case 'SX':
      return Networks.sx
    case 'SOL':
      return Networks.sol
    case 'BASE':
      return Networks.base
    case 'AVAX':
      return Networks.avalanche
    case 'CELO':
      return Networks.celo
    default:
      return Networks.ethereum
  }
}

export const StableCoins = {
  CADC: 'CADC',
  USDC: 'USDC',
  // USDT: 'USDT',
  // DAI: 'DAI',
  // BUSD: 'BUSD',
} as const

export type StableCoinValue = (typeof StableCoins)[keyof typeof StableCoins]

export const isStableCoin = (x: any): x is StableCoinValue =>
  Object.values(StableCoins).includes(x)

export function getStableCoinType(abr: string) {
  switch (abr) {
    case 'USDC':
      return StableCoins.USDC
    case 'CADC':
      return StableCoins.CADC
    default:
      return StableCoins.USDC
  }
}

export const supportedCurrencies: Record<NetworkValue, StableCoinValue[]> = {
  [Networks.ethereum]: [StableCoins.CADC, StableCoins.USDC],
  [Networks.arbitrum]: [StableCoins.CADC, StableCoins.USDC],
  [Networks.polygon]: [StableCoins.CADC, StableCoins.USDC],
  [Networks.optimism]: [StableCoins.USDC],
  [Networks.sx]: [StableCoins.USDC],
  [Networks.sol]: [StableCoins.USDC],
  [Networks.binance]: [StableCoins.USDC],
  [Networks.base]: [StableCoins.CADC, StableCoins.USDC],
  [Networks.avalanche]: [StableCoins.USDC],
  [Networks.celo]: [StableCoins.USDC],
}

export const getSupportedCurrencies = (
  enableCadcSolana: boolean
): Record<NetworkValue, StableCoinValue[]> =>
  enableCadcSolana &&
  SolanaTokenConfigByCluster[SOLANA_CLUSTER][StableCoins.CADC].deployed
    ? {
        ...supportedCurrencies,
        [Networks.sol]: [StableCoins.CADC, StableCoins.USDC],
      }
    : supportedCurrencies

export const FiatCurrency = {
  CAD: 'CAD',
  USD: 'USD',
}

export const isFiat = (x: any) => Object.values(FiatCurrency).includes(x)

export const PaymentTypes = {
  REQUEST: 'request',
  ETRANSFER: 'e-transfer',
  WIRE: 'wire',
} as const

export type PaymentType = (typeof PaymentTypes)[keyof typeof PaymentTypes]

export interface CoinOption {
  value: string
  name: string
  description: string
  shortDescription?: string
  image?: string
}

export interface StableCoinOption extends CoinOption {
  value: StableCoinValue
}

const INTERAC_PAYMENT_OPTION: CoinOption = {
  value: PaymentTypes.REQUEST,
  name: 'CAD',
  description: 'Interac Request Money Transfer',
  shortDescription: 'Interac',
  image: CAD,
}

export const WIRE_PAYMENT_OPTION: CoinOption = {
  value: PaymentTypes.WIRE,
  name: 'CAD',
  description: 'Wire Transfer',
  shortDescription: 'Wire',
  image: CAD,
}

export const EFT_PAYMENT_OPTION: CoinOption = {
  value: 'eft',
  name: 'CAD',
  description: 'EFT Transfer',
  shortDescription: 'EFT',
  image: CAD,
}

export const getPaymentOptions = (includeWire: boolean): CoinOption[] =>
  includeWire
    ? [INTERAC_PAYMENT_OPTION, WIRE_PAYMENT_OPTION]
    : [INTERAC_PAYMENT_OPTION]

export const allTokenOptions: StableCoinOption[] = [
  {
    value: StableCoins.CADC,
    name: 'CADC',
    description: '',
    image: CADC,
  },
  {
    value: StableCoins.USDC,
    name: 'USDC',
    description: '',
    image: USDC,
  },
  // {
  //   value: StableCoins.DAI,
  //   name: 'DAI',
  //   description: 'Coin',
  // },
  // {
  //   value: StableCoins.BUSD,
  //   name: 'BUSD',
  //   description: 'Coin',
  // },
  // {
  //   value: StableCoins.USDT,
  //   name: 'USDT',
  //   description: 'Coin',
  // },
]

export const stableCoinLogos = {
  [`${StableCoins.CADC}${Networks.arbitrum}`]: CADARB,
  [`${StableCoins.CADC}${Networks.base}`]: CADBASE,
  [`${StableCoins.CADC}${Networks.sol}`]: CADSOL,
  [`${StableCoins.CADC}${Networks.ethereum}`]: CADETH,
  [`${StableCoins.CADC}${Networks.polygon}`]: CADPOLY,
  [`${StableCoins.USDC}${Networks.polygon}`]: USDPOLY,
  [`${StableCoins.USDC}${Networks.ethereum}`]: USDETH,
  [`${StableCoins.USDC}${Networks.arbitrum}`]: USDARB,
  [`${StableCoins.USDC}${Networks.optimism}`]: USDCOPTI,
  [`${StableCoins.USDC}${Networks.sx}`]: USDCSX,
  [`${StableCoins.USDC}${Networks.sol}`]: USDCSOL,
  [`${StableCoins.USDC}${Networks.binance}`]: USDCBN,
  [`${StableCoins.USDC}${Networks.base}`]: USDCBASE,
  [`${StableCoins.USDC}${Networks.avalanche}`]: USDCAVAX,
  [`${StableCoins.USDC}${Networks.celo}`]: USDCCELO,
}

export const MAX_24_999_SELF_SERVE_AMOUNT = 24999
export const MAX_9999_SELF_SERVE_AMOUNT = 9999

type USDCNetworks = Extract<
  NetworkValue,
  'ETH' | 'POLY' | 'ARB' | 'BSC' | 'OP' | 'BASE' | 'AVAX' | 'CELO'
>

const UsdcTokenAddresses: Record<USDCNetworks, `0x${string}`> = {
  [Networks.ethereum]: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
  [Networks.polygon]: '0x3c499c542cef5e3811e1192ce70d8cc03d5c3359',
  [Networks.arbitrum]: '0xaf88d065e77c8cC2239327C5EDb3A432268e5831',
  [Networks.binance]: '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d',
  [Networks.optimism]: '0x0b2C639c533813f4Aa9D7837CAf62653d097Ff85',
  [Networks.base]: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
  [Networks.avalanche]: '0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E',
  [Networks.celo]: '0xcebA9300f2b948710d2653dD7B07f33A8B32118C',
} as const

const UsdcTokenDecimals: Record<USDCNetworks, number> = {
  ETH: 6,
  POLY: 6,
  ARB: 6,
  BSC: 18,
  OP: 6,
  BASE: 6,
  AVAX: 6,
  CELO: 6,
} as const

type EvmTokenContract = {
  address: `0x${string}`
  decimals: number
}

const getEvmTokenContract = ({
  token,
  network,
}: {
  token: StableCoinValue
  network: NetworkValue
}): EvmTokenContract | undefined => {
  if (token === StableCoins.CADC) {
    const contract = getTokenContractsFor({
      env: 'mainnet',
      network,
      symbol: StableCoins.CADC,
    }).find(({ lifecycle }) => lifecycle === 'current')
    return (
      contract && {
        address: contract.address as `0x${string}`,
        decimals: contract.decimals,
      }
    )
  }

  if (!(network in UsdcTokenAddresses)) return undefined
  return {
    address: UsdcTokenAddresses[network as USDCNetworks],
    decimals: UsdcTokenDecimals[network as USDCNetworks],
  }
}

const getCadcAddressesByNetwork = (
  lifecycle: TokenLifecycle
): Partial<Record<NetworkValue, string>> =>
  Object.values(Networks).reduce<Partial<Record<NetworkValue, string>>>(
    (addresses, network) => {
      const address = getCadcContractAddress({
        env: 'mainnet',
        network,
        lifecycle,
      })
      return address ? { ...addresses, [network]: address } : addresses
    },
    {}
  )

export const NewCadcTokenAddresses = getCadcAddressesByNetwork('current')

export const getHasRegulatedCadcContract = (network: NetworkValue): boolean =>
  Boolean(NewCadcTokenAddresses[network])

const legacyCadcTokenAddresses = getCadcAddressesByNetwork('legacy')

export const getLegacyCadcTokenAddress = (
  network: NetworkValue
): string | undefined => legacyCadcTokenAddresses[network]

export const BlockExplorerUrls: Record<NetworkValue, string> =
  BLOCK_EXPLORER_URLS

export { getTokenExplorerUrl }

export const ChainIds = {
  ETH: 1,
  POLY: 137,
  ARB: 42161,
  BSC: 56,
  OP: 10,
  BASE: 8453,
  AVAX: 43114,
  CELO: 42220,
} as const

export const SOLANA_TOKEN_PROGRAM =
  'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA'
export const SOLANA_TOKEN_2022_PROGRAM =
  'TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb'

export const SOLANA_TOKEN_NOT_DEPLOYED = 'TODO_NOT_DEPLOYED_ON_SOLANA'

export type SolanaTokenConfigEntry = {
  mint: string
  decimals: number
  tokenProgram: typeof SOLANA_TOKEN_PROGRAM | typeof SOLANA_TOKEN_2022_PROGRAM
  destinationAta: string
  deployed: boolean
}

export const SolanaTokenConfigByCluster: Record<
  SolanaCluster,
  Record<StableCoinValue, SolanaTokenConfigEntry>
> = {
  'mainnet-beta': {
    [StableCoins.USDC]: {
      mint: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',
      decimals: 6,
      tokenProgram: SOLANA_TOKEN_PROGRAM,
      destinationAta: 'GDjPSohW1FNbj9q12d6vDDJWd31hXBMakRAqPB6tji9c',
      deployed: true,
    },
    [StableCoins.CADC]: {
      mint: '52spLAAFjKMwhNJ3TL2PUAUGYGgXsbvipP4whRtBp82S',
      decimals: 6,
      tokenProgram: SOLANA_TOKEN_PROGRAM,
      destinationAta: 'G14nRVNTpjmmRrRCW35rkSHKpaxuDqMQGWE2vdjeJk7P',
      deployed: true,
    },
  },
  devnet: {
    [StableCoins.USDC]: {
      mint: '4zMMC9srt5Ri5X14GAgXhaHii3GnPAEERYPJgZJDncDU',
      decimals: 6,
      tokenProgram: SOLANA_TOKEN_PROGRAM,
      destinationAta: '2PAfFbBQbU8GDxwA9ufuAnQUhe8X2EFggTsjQeHUAbQr',
      deployed: true,
    },
    [StableCoins.CADC]: {
      mint: '52spLAAFjKMwhNJ3TL2PUAUGYGgXsbvipP4whRtBp82S',
      decimals: 6,
      tokenProgram: SOLANA_TOKEN_PROGRAM,
      destinationAta: 'BD9CGBL1uJM46GYC59AR5KBHQ1ZbaJ6U4xREzmm7DzKG',
      deployed: true,
    },
  },
}

export function getSolanaTokenConfig({
  cluster,
  tokenSymbol,
}: {
  cluster: SolanaCluster
  tokenSymbol: string
}): SolanaTokenConfigEntry {
  const [baseToken] = tokenSymbol.split('-')

  if (!isStableCoin(baseToken)) {
    throw new Error(`Invalid token: ${baseToken}`)
  }

  const config = SolanaTokenConfigByCluster[cluster][baseToken]

  if (!config.deployed) {
    throw new Error(`${baseToken} is not yet available on Solana`)
  }

  return config
}

export function getTokenAddress(
  chainId: number,
  tokenSymbol: string
): `0x${string}` {
  const [baseToken, network] = tokenSymbol.split('-')

  if (!isStableCoin(baseToken)) {
    throw new Error(`Invalid token: ${baseToken}`)
  }

  // Validate the chainId matches the network in the token symbol
  validateChainIdNetwork(chainId, network)

  const contract = getEvmTokenContract({
    token: baseToken,
    network: getNetworkFromChainId(chainId),
  })

  if (!contract) {
    throw new Error(
      `${baseToken} is not supported on network with chainId ${chainId}`
    )
  }

  return contract.address
}

// Helper function to get network from chainId
function getNetworkFromChainId(chainId: number): NetworkValue {
  const entry = Object.entries(ChainIds).find(([, id]) => id === chainId)
  if (!entry) {
    throw new Error(`Unsupported chainId: ${chainId}`)
  }
  return entry[0] as NetworkValue
}

// Helper to verify chainId matches network string
function validateChainIdNetwork(chainId: number, network: string): void {
  const expectedChainId = ChainIds[network as keyof typeof ChainIds]
  if (expectedChainId !== chainId) {
    throw new Error(`ChainId ${chainId} does not match network ${network}`)
  }
}

export function getTokenDecimals(tokenSymbol: string): number {
  const [baseToken, network] = tokenSymbol.split('-')

  if (!isStableCoin(baseToken)) {
    throw new Error(`Invalid token: ${baseToken}`)
  }

  const contract = getEvmTokenContract({
    token: baseToken,
    network: getNetwork(network),
  })

  if (!contract) {
    throw new Error(
      `No decimal configuration found for ${baseToken} on ${network} network`
    )
  }

  return contract.decimals
}

export function getChainId(tokenSymbol: string): number {
  // Extract network part from token symbol (e.g., "USDC-ETH" -> "ETH")
  const [, network] = tokenSymbol.split('-')

  if (!network) {
    throw new Error(`Invalid token symbol format: ${tokenSymbol}`)
  }

  const chainId = ChainIds[network as keyof typeof ChainIds]

  if (chainId === undefined) {
    throw new Error(`Unsupported network: ${network}`)
  }

  return chainId
}

export function isTokenNetworkSupported(
  token: string,
  network: string
): boolean {
  try {
    if (!isStableCoin(token)) {
      return false
    }

    return Boolean(getEvmTokenContract({ token, network: getNetwork(network) }))
  } catch {
    return false
  }
}

export const truncateAddress = (address: string): string => {
  if (!address) return ''
  return `${address.slice(0, 6)}...${address.slice(-4)}`
}

export const isNetworkSupported = (network: string): boolean => {
  const supportedNetworks = [
    'ETH',
    'POLY',
    'ARB',
    'BSC',
    'OP',
    'BASE',
    'AVAX',
    'CELO',
    'SOL',
  ]
  return supportedNetworks.includes(network)
}

export const getNetworkName = (chainSymbol: string): string => {
  const networkNames: Record<string, string> = {
    ETH: 'Ethereum',
    POLY: 'Polygon',
    ARB: 'Arbitrum',
    BSC: 'BNB Chain',
    OP: 'Optimism',
    BASE: 'Base',
    AVAX: 'Avalanche',
    CELO: 'Celo',
    SX: 'SX',
    SOL: 'Solana',
  }
  return networkNames[chainSymbol] || chainSymbol
}
